import { useEffect, useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import PropTypes from "prop-types";
import "@css/SiteHealthStatus.css";
import { useApi } from "@hooks/useApi";
import { formatDateTime } from "@utils/index";

const REFRESH_INTERVAL = 60000; // 1 minute
const DAYS_SECONDS = 86400;
const HOURS_SECONDS = 3600;
const MINUTES_SECONDS = 60;

const getStatusColor = (status) => {
    if (status === "ok") {
        return "#4caf50"; // green
    }
    if (status === "degraded") {
        return "#ff9800"; // orange
    }
    return "#f44336"; // red
};

const formatUptime = (seconds) => {
    if (!seconds) {
        return "N/A";
    }
    const days = Math.floor(seconds / DAYS_SECONDS);
    const hours = Math.floor((seconds % DAYS_SECONDS) / HOURS_SECONDS);
    const mins = Math.floor((seconds % HOURS_SECONDS) / MINUTES_SECONDS);
    if (days > 0) {
        return `${days}d ${hours}h`;
    }
    if (hours > 0) {
        return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
};

const STATUS_MAP = {
    ok: "Хорошо",
    degraded: "Ухудшено",
    error: "Ошибка",
};

const DB_STATUS_MAP = {
    connected: "Подключено",
    disconnected: "Отключено",
};

const CONNECTION_MAP = {
    true: "Да",
    false: "Нет",
};

const formatDate = (dateString) => formatDateTime(dateString, { timeStyle: "medium" });

const formatBuildDate = (dateString) => {
    if (!dateString) return null;
    try {
        const d = new Date(dateString);
        if (Number.isNaN(d.getTime())) return dateString;
        return formatDateTime(d);
    } catch {
        return dateString;
    }
};

const SiteHealthStatus = () => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);
    const api = useApi();

    // Build info: prefer Vite build-time env, fall back to /meta.json
    const staticBuildTime = import.meta.env.VITE_BUILD_TIME || import.meta.env.VITE_BUILD_DATE || null;
    const staticBuildVersion = import.meta.env.VITE_APP_VERSION || import.meta.env.VITE_BUILD_VERSION || null;
    const { data: buildMeta } = useQuery({
        queryKey: ["siteBuildMeta"],
        queryFn: async () => {
            // Intentional direct fetch: this reads local static build metadata,
            // not backend API data, so useApi is intentionally not used here.
            const res = await fetch(`/meta.json?ts=${Date.now()}`, {
                cache: "no-store",
            });
            if (!res.ok) {
                return null;
            }
            const data = await res.json();
            return {
                buildTime:
                    data.buildTime
                    || data.build_time
                    || data.BUILD_TIME
                    || null,
                buildVersion:
                    data.version
                    || data.appVersion
                    || data.APP_VERSION
                    || null,
            };
        },
        staleTime: 0,
        refetchInterval: REFRESH_INTERVAL,
        refetchOnWindowFocus: true,
        retry: 0,
    });

    const buildTime = buildMeta?.buildTime || staticBuildTime;
    const buildVersion = buildMeta?.buildVersion || staticBuildVersion;

    const { data: health, error } = useQuery({
        queryKey: ["siteHealth"],
        queryFn: async () => {
            const response = await api.get(
                "site-health",
            );
            return response || {
                status: "error",
                ok: false,
                timestamp: new Date().toISOString(),
            };
        },
        refetchInterval: REFRESH_INTERVAL, // Refetch every 1 minute
        refetchOnWindowFocus: true, // Refetch when window regains focus
        staleTime: 30000, // Consider data stale after 30 seconds
        retry: 1,
    });

    // Show error status if fetch fails
    const displayHealth = error ? {
        status: "error",
        ok: false,
        timestamp: new Date().toISOString(),
        errorMessage: error.message || "Ошибка подключения",
    } : health;

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                containerRef.current
                && !containerRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    const handleToggle = () => {
        setIsOpen(!isOpen);
    };

    if (!displayHealth) {
        return null;
    }

    return (
        <div className="site-health-container" ref={containerRef}>
            <button
                className="health-indicator-btn"
                onClick={handleToggle}
                onMouseEnter={() => {
                    setIsOpen(true);
                }}
                onMouseLeave={() => {
                    setIsOpen(false);
                }}
                title="Состояние сайта"
            >
                <span
                    className="health-circle"
                    style={{ backgroundColor: getStatusColor(displayHealth.status) }}
                />
                <span className="health-status-label">{STATUS_MAP[displayHealth.status] || displayHealth.status}</span>
            </button>

            {isOpen ? <HealthDropdown
                health={displayHealth}
                onMouseEnter={() => {
                    setIsOpen(true);
                }}
                onMouseLeave={() => {
                    setIsOpen(false);
                }}
                buildTime={buildTime}
                buildVersion={buildVersion}
            /> : null}
        </div>
    );
};

const DatabaseMetrics = ({ database }) => {
    if (!database) {
        return null;
    }
    return (
        <div className="metric-section">
            <div className="section-title">
                База данных
            </div>
            <div className="metric-row">
                <span className="metric-label">
                    Статус:
                </span>
                <span className="metric-value">
                    {(database.connected ? DB_STATUS_MAP.connected : DB_STATUS_MAP.disconnected)}
                </span>
            </div>
        </div>
    );
};

DatabaseMetrics.propTypes = {
    database: PropTypes.shape({
        status: PropTypes.string,
        connected: PropTypes.bool,
    }),
};

const RedisMetrics = ({ redis }) => {
    if (!redis) {
        return null;
    }
    return (
        <div className="metric-section">
            <div className="section-title">Redis</div>
            <div className="metric-row">
                <span className="metric-label">
                    Подключено:
                </span>
                <span className="metric-value">
                    {CONNECTION_MAP[String(redis.connected)]}
                </span>
            </div>
            {redis.ping !== undefined && (
                <div className="metric-row">
                    <span className="metric-label">
                        Пинг:
                    </span>
                    <span className="metric-value">
                        {redis.ping}ms
                    </span>
                </div>
            )}
        </div>
    );
};

RedisMetrics.propTypes = {
    redis: PropTypes.shape({
        connected: PropTypes.bool,
        ping: PropTypes.number,
    }),
};



const LichessMetrics = ({ lastLichessPoll }) => {
    if (!lastLichessPoll) {
        return null;
    }
    return (
        <div className="metric-section">
            <div className="section-title">
                Lichess
            </div>
            {lastLichessPoll.timestamp ? <div className="metric-row">
                <span className="metric-label">
                    Последний опрос:
                </span>
                <span className="metric-value">
                    {formatDate(
                        lastLichessPoll
                            .timestamp
                    )}
                </span>
            </div> : null}
            {/* show if rate limited */ }
            {lastLichessPoll.rateLimited !== undefined && (
                <div className="metric-row">
                    <span className="metric-label">
                        Ограничен по скорости:
                    </span>
                    <span className="metric-value">
                        {lastLichessPoll.rateLimited ? "Да" : "Нет"}
                    </span>
                </div>
            )}
            {lastLichessPoll.usersChecked
                !== undefined && (
                <div className="metric-row">
                    <span className="metric-label">
                        Проверено пользователей:
                    </span>
                    <span className="metric-value">
                        {
                            lastLichessPoll
                                .usersChecked
                        }
                    </span>
                </div>
            )}
        </div>
    );
};

LichessMetrics.propTypes = {
    lastLichessPoll: PropTypes.shape({
        timestamp: PropTypes.string,
        usersChecked: PropTypes.number,
        rateLimited: PropTypes.bool,
    }),
};

const buildDateText = (version, time) => {
    const separator = version ? " @ " : "";
    return separator + formatBuildDate(time);
};

const HealthDropdown = ({
    health,
    onMouseEnter,
    onMouseLeave,
    buildTime,
    buildVersion,
}) => (
    <section
        className="health-dropdown"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
    >
        <div className="health-dropdown-content">
            <div className="health-header">
                <h3>Состояние сайта</h3>
            </div>

            <div className="health-metrics">
                {/* Status */}
                <div className="metric-row">
                    <span className="metric-label">Статус:</span>
                    <span
                        className="metric-value status-badge"
                        style={{
                            color: getStatusColor(health.status),
                        }}
                    >
                        {STATUS_MAP[health.status] || health.status}
                    </span>
                </div>

                {/* Error Message */}
                {health.errorMessage ? <div className="metric-row">
                    <span className="metric-label">
                        Ошибка:
                    </span>
                    <span className="metric-value error-message">
                        {health.errorMessage}
                    </span>
                </div> : null}

                {/* Uptime */}
                {health.uptime !== undefined && (
                    <div className="metric-row">
                        <span className="metric-label">
                            Время работы:
                        </span>
                        <span className="metric-value">
                            {formatUptime(health.uptime)}
                        </span>
                    </div>
                )}

                <DatabaseMetrics database={health.database} />
                <RedisMetrics redis={health.redis} />
                <LichessMetrics lastLichessPoll={ { ...health.lastLichessPoll, ...health.lichessApi } } />

                {/* Timestamp */}
                {health.timestamp ? <div className="metric-row timestamp">
                    <span className="metric-label">
                        Обновлено:
                    </span>
                    <span className="metric-value">
                        {formatDate(health.timestamp)}
                    </span>
                </div> : null}
                {(buildTime || buildVersion) ? <div className="metric-row build-info">
                    <span className="metric-label">Сборка:</span>
                    <span className="metric-value">
                        {buildVersion ? `${buildVersion}` : ""}
                        {buildTime ? buildDateText(buildVersion, buildTime) : ""}
                    </span>
                </div> : null}
            </div>
        </div>
    </section>
);

HealthDropdown.propTypes = {
    health: PropTypes.shape({
        status: PropTypes.string.isRequired,
        ok: PropTypes.bool,
        timestamp: PropTypes.string,
        uptime: PropTypes.number,
        errorMessage: PropTypes.string,
        database: PropTypes.shape({
            status: PropTypes.string,
            connected: PropTypes.bool,
        }),
        redis: PropTypes.shape({
            connected: PropTypes.bool,
            ping: PropTypes.number,
        }),
        memory: PropTypes.shape({
            heapUsed: PropTypes.number,
            heapTotal: PropTypes.number,
        }),
        lastLichessPoll: PropTypes.shape({
            timestamp: PropTypes.string,
            usersChecked: PropTypes.number,
        }),
        // Merged into lastLichessPoll before it reaches <LichessMetrics />
        lichessApi: PropTypes.shape({
            rateLimited: PropTypes.bool,
        }),
    }).isRequired,
    onMouseEnter: PropTypes.func.isRequired,
    onMouseLeave: PropTypes.func.isRequired,
    buildTime: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    buildVersion: PropTypes.string,
};

export default SiteHealthStatus;

